<script>
import { defineAsyncComponent } from 'vue';
import WidgetHead from './WidgetHead.vue';
import WidgetBody from './WidgetBody.vue';
import InputRadioGroup from 'dashboard/routes/dashboard/settings/inbox/components/InputRadioGroup.vue';
import { useBranding } from 'shared/composables/useBranding';
import { buildHttpUrl } from 'shared/helpers/URLHelper';
import FluentIcon from 'shared/components/FluentIcon/Index.vue';

export default {
  name: 'Widget',

  components: {
    WidgetHead,
    WidgetBody,
    InputRadioGroup,
    FluentIcon,
  },

  props: {
    welcomeHeading: { type: String, default: '' },
    welcomeTagline: { type: String, default: '' },
    websiteName: { type: String, required: true },
    logo: { type: String, default: '' },
    isOnline: { type: Boolean, default: true },
    replyTime: { type: String, default: '' },
    color: { type: String, default: '' },
    widgetBubblePosition: { type: String, default: '' },
    widgetBubbleLauncherTitle: { type: String, default: '' },
    widgetBubbleType: { type: String, default: '' },
    widgetTemplate: { type: String, default: 'Template1' },
    widgetFontFamily: { type: String, default: 'Inter' },
    widgetRedirectUrlName: { type: String, default: '' },
    widgetRedirectUrlLink: { type: String, default: '' },
    displayBranding: { type: Boolean, default: true },
    displayDisclaimer: { type: Boolean, default: true },
    disclaimerMessage: { type: String, default: '' },
    brandingText: { type: String, default: '' },
    brandingLink: { type: String, default: '' },
    brandingImageUrl: { type: String, default: '' },
  },

  setup() {
    const { replaceInstallationName } = useBranding();
    return { replaceInstallationName };
  },

  data() {
    return {
      widgetScreens: [
        {
          id: 'default',
          title: this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_SCREEN.DEFAULT'),
          checked: true,
        },
        {
          id: 'chat',
          title: this.$t('INBOX_MGMT.WIDGET_BUILDER.WIDGET_SCREEN.CHAT'),
          checked: false,
        },
      ],
      isDefaultScreen: true,
      isWidgetVisible: true,
    };
  },

  computed: {
    footerComponent() {
      const templateNum = this.widgetTemplate.replace('Template', '');
      const footerName = `WidgetFooter${templateNum}`;

      return defineAsyncComponent(() =>
        import(`./widgetFooter/${footerName}/index.vue`).catch(
          () => import('./widgetFooter/WidgetFooter1/index.vue')
        )
      );
    },

    getWidgetConfig() {
      return {
        welcomeHeading: this.welcomeHeading,
        welcomeTagline: this.welcomeTagline,
        websiteName: this.websiteName,
        logo: this.logo,
        isDefaultScreen: this.isDefaultScreen,
        isOnline: this.isOnline,
        replyTime: this.replyTimeText,
        color: this.color,
        widgetTemplate: this.widgetTemplate,
        widgetRedirectUrlName: this.widgetRedirectUrlName,
        widgetRedirectUrlLink: this.widgetRedirectUrlLink,
        widgetFontFamily: this.widgetFontFamily,
      };
    },

    replyTimeText() {
      const predefinedOptions = [
        'in_a_few_minutes',
        'in_a_few_hours',
        'in_a_day',
      ];

      if (!predefinedOptions.includes(this.replyTime)) {
        return this.replyTime;
      }

      switch (this.replyTime) {
        case 'in_a_few_minutes':
          return this.$t(
            'INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_MINUTES'
          );
        case 'in_a_day':
          return this.$t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_DAY');
        default:
          return this.$t('INBOX_MGMT.WIDGET_BUILDER.REPLY_TIME.IN_A_FEW_HOURS');
      }
    },

    getBubblePositionStyle() {
      return {
        justifyContent:
          this.widgetBubblePosition === 'left' ? 'flex-start' : 'flex-end',
      };
    },

    isBubbleExpanded() {
      return (
        !this.isWidgetVisible && this.widgetBubbleType === 'expanded_bubble'
      );
    },

    getWidgetBubbleLauncherTitle() {
      return this.isWidgetVisible || this.widgetBubbleType === 'standard'
        ? ''
        : this.widgetBubbleLauncherTitle;
    },

    brandRedirectURL() {
      return buildHttpUrl(this.brandingLink) || '';
    },

    brandingImageSrc() {
      return buildHttpUrl(this.brandingImageUrl) || '';
    },
  },

  methods: {
    handleScreenChange(item) {
      this.isDefaultScreen = item.id === 'default';
    },

    toggleWidget() {
      this.isWidgetVisible = !this.isWidgetVisible;
      this.isDefaultScreen = true;
    },
  },
};
</script>

<template>
  <div>
    <!-- Screen Selector -->
    <div v-if="isWidgetVisible" class="flex flex-col items-center">
      <InputRadioGroup
        name="widget-screen"
        :items="widgetScreens"
        :action="handleScreenChange"
      />
    </div>

    <!-- Widget Box -->
    <div
      v-if="isWidgetVisible"
      class="widget-wrapper flex flex-col rounded-lg shadow-md bg-n-slate-2 dark:bg-n-solid-1 h-[31.25rem] w-80"
      :style="{ fontFamily: widgetFontFamily }"
    >
      <WidgetHead :config="getWidgetConfig" />

      <!-- Body + Footer Area -->
      <div class="flex flex-col flex-1">
        <!-- Body -->
        <WidgetBody
          v-if="!getWidgetConfig.isDefaultScreen"
          :config="getWidgetConfig"
        />

        <!-- Footer (POSITION CONTROL HAPPENS HERE) -->
        <component :is="footerComponent" :config="getWidgetConfig" />

        <!-- Branding stays normal — no layout hacks -->
        <div
          v-if="displayBranding || displayDisclaimer"
          class="py-2.5 flex justify-center items-center"
        >
          <a
            v-if="displayBranding"
            :href="brandRedirectURL"
            target="_blank"
            rel="noreferrer noopener nofollow"
            class="flex items-center gap-0.5 text-n-slate-11 cursor-pointer filter grayscale opacity-90 hover:grayscale-0 hover:opacity-100 text-xxs"
          >
            <img
              v-if="brandingImageSrc"
              class="max-w-2.5 max-h-2.5 object-contain"
              :src="brandingImageSrc"
            />
            <span>
              {{ replaceInstallationName(brandingText) }}
            </span>
          </a>
          <span
            v-if="displayDisclaimer"
            :title="disclaimerMessage"
            class="ltr:ml-1 rtl:mr-1 inline-flex items-center"
          >
            <FluentIcon
              icon="info"
              type="outline"
              size="14"
              class="text-n-slate-11 hover:text-n-slate-12 transition-colors cursor-help"
            />
          </span>
        </div>
      </div>
    </div>

    <!-- Bubble Button -->
    <div class="flex mt-4 w-[320px]" :style="getBubblePositionStyle">
      <button
        class="relative flex items-center justify-center rounded-full cursor-pointer"
        :style="{ background: color }"
        :class="
          isBubbleExpanded
            ? 'w-auto font-medium text-base text-white h-12 px-4'
            : 'w-16 h-16'
        "
        @click="toggleWidget"
      >
        <img
          v-if="!isWidgetVisible"
          src="~dashboard/assets/images/bubble-logo.svg"
          alt=""
          draggable="false"
          class="w-6 h-6 mx-auto"
        />

        <div v-if="isBubbleExpanded" class="ltr:pl-2.5 rtl:pr-2.5">
          {{ getWidgetBubbleLauncherTitle }}
        </div>

        <div v-if="isWidgetVisible" class="relative">
          <div class="absolute w-0.5 h-8 rotate-45 -translate-y-1/2 bg-white" />
          <div
            class="absolute w-0.5 h-8 -rotate-45 -translate-y-1/2 bg-white"
          />
        </div>
      </button>
    </div>
  </div>
</template>
